import { createAsyncThunk } from '@reduxjs/toolkit';
import { logger, monitoring } from 'common/services';
import { getMediaDevices, queryBrowserPermission, tryGetUserMedia } from 'devices/utils';

import { DeviceKind, PermissionStatus } from '../core';
import { selectCameraPermission, selectMicrophonePermission } from './devices.selectors';
import { devicePermissionResolved } from './devices.slice';

import type { AppDispatch, RootState } from 'store';

// The concrete states resolution can land on — a subset of PermissionStatus: it never returns
// 'unknown' (it resolves away from it) or 'unsupported' (handled before this runs).
type ResolvedPermission = Exclude<PermissionStatus, 'unknown' | 'unsupported'>;

/**
 * Determine the ambient permission for a device kind WITHOUT triggering a browser prompt.
 * Uses the Permissions API when available, falling back to an `enumerateDevices` label
 * heuristic for Safari < 16. When the browser reports `granted` we additionally verify
 * OS-level access (which never prompts) so a macOS/Windows privacy block surfaces as
 * `system_denied` rather than a false `granted`.
 *
 * Moved here from `user/state` as the final step of ADR 0001: `devices/` now owns
 * resolution end-to-end and writes the devices slice directly.
 */
const resolveDevicePermission = async (kind: DeviceKind): Promise<ResolvedPermission> => {
  const browserState = await queryBrowserPermission(kind);
  const deviceKind = kind === 'microphone' ? 'audioinput' : 'videoinput';

  switch (browserState) {
    case 'granted': {
      // Permission is held. Only verify OS-level access when a device of this kind actually
      // exists — with none connected, getUserMedia fails with NotFoundError purely for lack of
      // hardware, which would mislabel a genuine grant as system_denied.
      const devices = await getMediaDevices();
      const hasDeviceOfKind = devices.some((d) => d.kind === deviceKind);
      if (!hasDeviceOfKind) {
        return 'granted';
      }

      const hasAccess = await tryGetUserMedia(kind);
      if (!hasAccess) {
        logger.warn(`[resolveDevicePermission] ${kind} denied at OS level`);
        monitoring.trackAction(`devices.${kind}.system_permission_denied`);
      }
      return hasAccess ? 'granted' : 'system_denied';
    }

    case 'denied':
      return 'denied';

    case 'prompt':
      return 'prompt';

    default: {
      // Permissions API unavailable (Safari < 16): infer from whether device labels are visible
      // (labels are only exposed once permission was granted, so getUserMedia won't prompt).
      const devices = await getMediaDevices();
      const hasLabeledDevices = devices.some((d) => d.kind === deviceKind && d.label);

      if (hasLabeledDevices) {
        const hasAccess = await tryGetUserMedia(kind);
        if (!hasAccess) {
          logger.warn(`[resolveDevicePermission] ${kind} denied (Permissions API unavailable)`);
          monitoring.trackAction(`devices.${kind}.permission_denied_on_verification`);
        }
        return hasAccess ? 'granted' : 'system_denied';
      }

      // No labels and no Permissions API — can't determine without prompting.
      return 'prompt';
    }
  }
};

// Dedup guards: two rapid resolve dispatches (initial load + an immediate devicechange) can both
// read 'unknown' from the same stale snapshot; the second awaits the first's in-flight promise
// instead of opening hardware twice. Module-level because the race spans separate thunk calls.
let micResolving: Promise<ResolvedPermission> | null = null;
let camResolving: Promise<ResolvedPermission> | null = null;

type ResolveOptions = { audioOnly?: boolean } | undefined;
type ThunkApi = { state: RootState; dispatch: AppDispatch };

/**
 * Resolve ambient permission for the relevant kinds (microphone always; camera unless
 * `audioOnly`) and write the result into the devices slice. Resolution runs once per kind —
 * only while the slice permission is still `'unknown'`; later calls are no-ops, so this is safe
 * to dispatch on mount and on every `devicechange`.
 */
export const resolveDevicePermissions = createAsyncThunk<void, ResolveOptions, ThunkApi>(
  '[devices] resolve permissions',
  async (options, { dispatch, getState }) => {
    const state = getState();

    if (selectMicrophonePermission(state) === 'unknown') {
      if (!micResolving) micResolving = resolveDevicePermission('microphone');
      try {
        dispatch(devicePermissionResolved({ kind: 'microphone', status: await micResolving }));
      } finally {
        micResolving = null;
      }
    }

    if (!options?.audioOnly && selectCameraPermission(state) === 'unknown') {
      if (!camResolving) camResolving = resolveDevicePermission('camera');
      try {
        dispatch(devicePermissionResolved({ kind: 'camera', status: await camResolving }));
      } finally {
        camResolving = null;
      }
    }
  }
);
